import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import _ from 'lodash';
import classnames from 'classnames';
import {
  Collapse,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { updateFile } from '../../actions/Files';

import UploadFileTaxonomy from '../Upload/FileTaxonomy';
import UpgradePlanButton from '../UpgradePlanButton';
import ProcessingIcon from '../ProcessingIcon';

import {
  userCan,
  userIsAdmin,
} from '../../helpers/Auth';
import {
  eventSubscribe,
  eventUnsubscribe,
} from '../../helpers/Events';
import { userHasFeature } from '../../helpers/Features';
import {
  isFileProcessing,
  isImageFile,
  isVideoFile,
} from '../../helpers/Files';
import { getSetting } from '../../helpers/Settings';

import useOutsideClick from '../../hooks/useOutsideClick';

const FileTaxonomy = ({
  dispatch,
  file,
  taxonomy,
  showEmptyFields,
  open,
  handleExpandTaxonomy,
  readOnly,
}) => {
  const intl = useIntl();

  const [editMode, setEditMode] = useState(false);
  const [editedFile, setEditedFile] = useState({ ...file });
  const [originalFile, setOriginalFile] = useState({ ...file });
  const [touched, setTouched] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [saving, setSaving] = useState(false);

  const elRef = useRef();
  const isMounted = useRef();

  useEffect(() => {
    isMounted.current = file.uuid;
    return () => {
      isMounted.current = false;
    };
  }, [file.uuid]);

  const getTerms = () => (
    editedFile.terms.filter(t => !t.blocked) ? _.uniqBy(editedFile.terms, 'id').filter(t => (t.taxonomy_id === taxonomy.id && !t.blocked)) : []
  );
  const terms = taxonomy.ai_tagging && !(
    (getSetting('ai_tagging') && isImageFile(file.type)) ||
    (getSetting('rekognition_videos') && isVideoFile(file.type))
  ) ? [] : getTerms();

  const taxonomyRequiredError =
    editedFile.errors && editedFile.errors.taxonomy &&
    editedFile.errors.taxonomy.includes(taxonomy.id);

  useEffect(() => {
    if (taxonomy.required && editedFile.terms &&
      editedFile.terms.filter(t => t.taxonomy_id === taxonomy.id).length === 0) {
      const fileTaxonomyErrors =
        editedFile.errors && editedFile.errors.taxonomy ? editedFile.errors.taxonomy : [];
      if (!(fileTaxonomyErrors.includes(taxonomy.id))) {
        fileTaxonomyErrors.push(taxonomy.id);
        setEditedFile({
          ...editedFile,
          errors: {
            ...editedFile.errors,
            taxonomy: fileTaxonomyErrors,
          },
        });
      }
    } else if (editedFile.errors && editedFile.errors.taxonomy) {
      const fileTaxonomyErrors =
        editedFile.errors.taxonomy.filter(t => (t !== taxonomy.id));
      if (editedFile.errors.taxonomy.length !== fileTaxonomyErrors.length) {
        setEditedFile({
          ...editedFile,
          errors: {
            ...editedFile.errors,
            taxonomy: fileTaxonomyErrors,
          },
        });
      }
    }
  }, [editedFile, taxonomy.id, taxonomy.required]);

  useEffect(() => {
    setEditedFile({ ...file });
  }, [file]);

  useEffect(() => {
    if (!showEmptyFields) {
      setUpdated(false);
    }
  }, [showEmptyFields]);

  const toggle = () => {
    if (!userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging)) return;
    handleExpandTaxonomy(taxonomy.id);
  };

  const openEdit = () => {
    if (!readOnly && !saving && userCan('file.edit', file) && !(terms.length === 0 && taxonomy.ai_tagging)) {
      setEditMode(true);
    }
  };

  const handleTaxonomyChange = (values, taxonomyId, removed) => {
    const newEditedFile = { ...editedFile };
    values.forEach((term) => {
      if (!newEditedFile.terms.find(t => (t.id === term.id))) {
        newEditedFile.terms.push(term);
      }
    });

    if (removed) {
      const removedIds = removed.map(rt => (parseInt(rt.value)));
      newEditedFile.terms = newEditedFile.terms.filter(t => (!removedIds.includes(t.id)));
    }

    setEditedFile(newEditedFile);
    setTouched(true);
  };

  const updateFileInState = (updatedFile) => {
    if (isMounted.current === updatedFile.uuid) {
      setEditedFile({ ...updatedFile });
      setOriginalFile({ ...updatedFile });
    }
  };

  const saveChanges = useCallback((e, switching = false) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!touched) {
      setEditMode(false);
      return;
    }

    if (taxonomyRequiredError) {
      if (!switching) {
        setEditMode(false);
      }
      return;
    }

    setEditMode(false);
    setSaving(true);

    const newEditedFile = { ...editedFile };

    if (taxonomy.ai_tagging) {
      const editedTermIds =
        newEditedFile.terms.filter(t => (t.taxonomy_id === taxonomy.id)).map(t => (t.id));
      newEditedFile.removedTerms =
        originalFile.terms.filter(t => (
          t.taxonomy_id === taxonomy.id && !editedTermIds.includes(t.id)
        )).map(rt => (rt.id));
    }

    newEditedFile.inline_edit = true;
    dispatch(updateFile(newEditedFile)).then(() => {
      setSaving(false);
      setTouched(false);
      setUpdated(true);
      if (!switching) {
        updateFileInState(newEditedFile);
      }
    });
  }, [
    dispatch,
    editedFile,
    originalFile,
    taxonomy,
    taxonomyRequiredError,
    touched,
  ]);

  useEffect(() => {
    const onFileSwitch = () => {
      if (editMode) {
        saveChanges(null, true);
      }
    };

    eventSubscribe('fileSwitch', onFileSwitch);
    return () => {
      eventUnsubscribe('fileSwitch', onFileSwitch);
    };
  }, [saveChanges, editMode]);

  const cancelChanges = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setEditedFile(originalFile);
    setEditMode(false);
  };

  useOutsideClick(elRef, (e) => {
    const { target } = e;

    if (
      editMode &&
      !(
        target.classList.contains('fas') ||
        target.classList.contains('file-tags-list__empty') ||
        target.classList.contains('tag-label') ||
        target.classList.contains('taxonomy-select__option') ||
        target.classList.contains('taxonomy-select__multi-value__remove') ||
        target.tagName === 'svg' ||
        target.tagName === 'path'
      )
    ) {
      saveChanges();
    }
  });

  if (
    terms.length === 0 &&
    !updated && !touched &&
    !showEmptyFields &&
    !(taxonomy.ai_tagging && isFileProcessing(file, 'labels'))
  ) {
    return null;
  }

  let fieldTitle = '';
  if (!editMode) {
    fieldTitle = intl.formatMessage({ defaultMessage: 'Click to edit' });
  }
  if (terms.length === 0 && taxonomy.ai_tagging) {
    fieldTitle = '';
  }

  return (
    <div className="file-taxonomy file-details-item">
      <div className="file-detail-header">
        <Button
          onClick={toggle}
          className={classnames('file-detail-title', { 'file-detail-title-disabled': !userHasFeature('custom-categories') && (!taxonomy.standard && !taxonomy.ai_tagging) })}
        >
          <span>
            <span>
              {taxonomy.plural}
              {taxonomy.ai_tagging && (
                <ProcessingIcon file={file} job="labels" />
              )}
              {taxonomy.required && (
                <span
                  className="required-asterisk"
                  id={`taxonomy-details-required-asterisk-${taxonomy.id}`}
                >
                  *
                  <UncontrolledTooltip fade={false} placement="bottom" target={`taxonomy-details-required-asterisk-${taxonomy.id}`}>
                    {intl.formatMessage({ defaultMessage: 'Required' })}
                  </UncontrolledTooltip>
                </span>
              )}
            </span>
            {(!taxonomy.standard && !taxonomy.ai_tagging) && (
              <UpgradePlanButton feature="custom-categories" />
            )}
          </span>
          <i className={classnames('fal', { 'fa-angle-up': open, 'fa-angle-down': !open })} />
        </Button>
      </div>
      <Collapse
        isOpen={open}
      >
        <div className="file-detail-body">
          <div
            className={classnames(
              'editable-field',
              'editable-field-taxonomy',
              {
                'editable-field-editing': editMode,
                'editable-field-disabled': !userCan('file.edit', file) || (terms.length === 0 && taxonomy.ai_tagging),
                'editable-field-empty': terms.length === 0,
                'editable-field-read-only': readOnly,
              },
            )}
            role="button"
            tabIndex={0}
            title={fieldTitle}
            ref={elRef}
            onKeyUp={openEdit}
            onClick={openEdit}
          >
            {editMode ? (
              <div className="editable-field-taxonomy-wrapper">
                <UploadFileTaxonomy
                  selectedFiles={[editedFile]}
                  taxonomy={taxonomy}
                  handleTaxonomyChange={handleTaxonomyChange}
                />
                <a
                  href="#save"
                  title={intl.formatMessage({ defaultMessage: 'Save' })}
                  onClick={saveChanges}
                  className={classnames({ disabled: taxonomyRequiredError })}
                >
                  <i className="fas fa-check" />
                </a>
                <a
                  href="#cancel"
                  title={intl.formatMessage({ defaultMessage: 'Cancel' })}
                  onClick={cancelChanges}
                >
                  <i className="fas fa-xmark" />
                </a>
              </div>
            ) : (
              <div className="file-tags-list tags-list">
                {terms.length > 0 ? (
                  terms.map(term => (
                    <span
                      key={`${taxonomy.id}-${term.id}`}
                      className={classnames('tag tag-label', { 'tag-blocked': term.blocked })}
                    >
                      {term.name}
                      {term.blocked && (<i className="ml-2 fas fa-octagon-xmark" />)}
                    </span>
                  ))
                ) : (
                  <span className="file-tags-list__empty">
                    {(userCan('file.edit', file) && !taxonomy.ai_tagging && !readOnly) ? (
                      intl.formatMessage({ defaultMessage: 'Click to add {taxonomy}' }, {
                        taxonomy: taxonomy.plural.toLowerCase(),
                      })
                    ) : (
                      <>
                        {taxonomy.ai_tagging ? (
                          <>
                            {isFileProcessing(file, 'labels') ? (
                              intl.formatMessage({ defaultMessage: 'Tags are processing...' })
                            ) : (
                              <>
                                {(
                                  (getSetting('ai_tagging') && isImageFile(file.type)) ||
                                  (getSetting('rekognition_videos') && isVideoFile(file.type))
                                ) ? (
                                    intl.formatMessage({ defaultMessage: 'No {taxonomy}' }, {
                                      taxonomy: taxonomy.plural.toLowerCase(),
                                    })
                                  ) : (
                                    <>
                                      {intl.formatMessage({ defaultMessage: 'AI tagging is disabled' })}
                                      {userIsAdmin() && (
                                        <Link to="/admin/settings/ai-tagging">
                                          {' '}{intl.formatMessage({ defaultMessage: 'Enable' })}
                                        </Link>
                                      )}
                                    </>
                                  )}
                              </>
                            )}
                          </>
                        ) : (
                          intl.formatMessage({ defaultMessage: 'No {taxonomy}' }, {
                            taxonomy: taxonomy.plural.toLowerCase(),
                          })
                        )}
                      </>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

FileTaxonomy.propTypes = {
  dispatch: PropTypes.func.isRequired,
  file: PropTypes.object.isRequired,
  taxonomy: PropTypes.object.isRequired,
  showEmptyFields: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  handleExpandTaxonomy: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileTaxonomy.defaultProps = {
  readOnly: false,
};

export default connect()(FileTaxonomy);
